import type {
  FlexiblePromoResolutionParams,
  PromoResolutionParams,
} from './types';

/**
 * Resolves the deduction amount from the appropriate promo based on priority
 *
 * Priority order:
 * 1. Compiled promo deduction amount
 * 2. Bundled discount deduction amount
 * 3. Single discount deduction amount
 *
 * @param params - Promo resolution parameters (accepts both camelCase and snake_case)
 * @param params.hasCompiledDiscount - Whether compiled promo is active
 * @param params.compiledPromo - The compiled promo object
 * @param params.hasBundledDiscount - Whether bundled discount applies
 * @param params.product - Product with discount/bundled promos
 * @returns The deduction amount in basis points
 * @throws Error if arrays are empty when expected to have values
 *
 * @example
 * const deductionAmount = resolvePromoDeductionAmount({
 *   hasCompiledDiscount: true,
 *   compiledPromo: { deductionAmount: 500, ... },
 *   hasBundledDiscount: false,
 *   product: { ... },
 * });
 * // Returns: 500
 */
export const resolvePromoDeductionAmount = (
  params: PromoResolutionParams | FlexiblePromoResolutionParams
): number => {
  switch (true) {
    case params.hasCompiledDiscount && !!params.compiledPromo:
      return (
        (params.compiledPromo as any).deductionAmount ||
        (params.compiledPromo as any).deduction_amount ||
        0
      );

    case params.hasBundledDiscount: {
      const bundledPromo =
        (params.product as any)?.bundledDiscountPromos?.[0] ||
        (params.product as any)?.bundled_discount_promos?.[0];
      if (!bundledPromo) {
        throw new Error(
          'Bundled discount flag set but no promo found in bundledDiscountPromos array'
        );
      }
      return bundledPromo.deductionAmount || bundledPromo.deduction_amount || 0;
    }

    default: {
      const singlePromo =
        (params.product as any)?.discountPromos?.[0] ||
        (params.product as any)?.discount_promos?.[0];
      if (!singlePromo) {
        throw new Error('Discount expected but discountPromos array is empty');
      }
      return singlePromo.deductionAmount || singlePromo.deduction_amount || 0;
    }
  }
};

/**
 * Resolves the isDiscount flag from the appropriate promo based on priority
 *
 * Priority order:
 * 1. Compiled promo isDiscount flag
 * 2. Bundled discount isDiscount flag
 * 3. Single discount isDiscount flag
 *
 * Note: When isDiscount is false, the promo represents other types
 * (e.g., free goods, bundles) and should not display discount badge
 *
 * @param params - Promo resolution parameters (accepts both camelCase and snake_case)
 * @param params.hasCompiledDiscount - Whether compiled promo is active
 * @param params.compiledPromo - The compiled promo object
 * @param params.hasBundledDiscount - Whether bundled discount applies
 * @param params.product - Product with discount/bundled promos
 * @returns Whether the promo is a discount type (true) or other type (false)
 * @throws Error if arrays are empty when expected to have values
 *
 * @example
 * const isDiscount = resolvePromoIsDiscount({
 *   hasCompiledDiscount: true,
 *   compiledPromo: { isDiscount: true, ... },
 *   hasBundledDiscount: false,
 *   product: { ... },
 * });
 * // Returns: true
 */
export const resolvePromoIsDiscount = (
  params: PromoResolutionParams | FlexiblePromoResolutionParams
): boolean => {
  switch (true) {
    case params.hasCompiledDiscount && !!params.compiledPromo:
      return (
        (params.compiledPromo as any).isDiscount ||
        (params.compiledPromo as any).is_discount ||
        false
      );

    case params.hasBundledDiscount: {
      const bundledPromo =
        (params.product as any)?.bundledDiscountPromos?.[0] ||
        (params.product as any)?.bundled_discount_promos?.[0];
      if (!bundledPromo) {
        throw new Error(
          'Bundled discount flag set but no promo found in bundledDiscountPromos array'
        );
      }
      return bundledPromo.isDiscount || bundledPromo.is_discount || false;
    }

    default: {
      const singlePromo =
        (params.product as any)?.discountPromos?.[0] ||
        (params.product as any)?.discount_promos?.[0];
      if (!singlePromo) {
        throw new Error('Discount expected but discountPromos array is empty');
      }
      return singlePromo.isDiscount || singlePromo.is_discount || false;
    }
  }
};

/**
 * Creates a promo display data object with all attributes from the resolved promo
 *
 * Combines deduction amount and isDiscount flag into a single display object
 * for convenient passing to display components
 *
 * @param params - Promo resolution parameters (accepts both camelCase and snake_case)
 * @returns Object with deductionAmount and isDiscount properties
 * @throws Error if arrays are empty when expected to have values
 *
 * @example
 * const promoDisplay = createPromoDisplayData({
 *   hasCompiledDiscount: true,
 *   compiledPromo: { deductionAmount: 500, isDiscount: true, ... },
 *   hasBundledDiscount: false,
 *   product: { ... },
 * });
 * // Returns: { deductionAmount: 500, isDiscount: true }
 */
export const createPromoDisplayData = (
  params: PromoResolutionParams | FlexiblePromoResolutionParams
): { deductionAmount: number; isDiscount: boolean } => {
  return {
    deductionAmount: resolvePromoDeductionAmount(params),
    isDiscount: resolvePromoIsDiscount(params),
  };
};
